<template>
  <div id="navbar" class="navbar">
    <div id="navbar_left_section" class="flex justify-center py-4 pl-3 pr-4">
      <ThemeImage
        id="superagi_logo"
        class="w-10 h-7 cursor-pointer"
        :icon="imageNames.SUPER_AGI_LOGO"
        alt="superagi_logo"
        @click="router.push(isUserAuthenticated ? '/i-assistant' : '/market')"
      />
    </div>

    <div id="navbar_center_section" class="w-full">
      <div
        v-if="billingSetupComplete && isUserAuthenticated"
        class="flex flex-row justify-between items-center w-full py-2 pr-[6px]"
      >
        <div id="nav_bar_left" class="flex flex-row gap-2">
          <div
            @click="goToSearch"
            v-if="!isSearchPage && isGlobalSearchEnabled()"
            class="w-[300px]"
          >
            <CustomInput
              format="text"
              v-model="searchValue"
              placeholder="Search SuperAGI"
              type="primary"
              :icon="imageNames.SEARCH_VARIANT1"
              size="size-4"
              alt="search_icon"
              @click="goToSearch"
              v-if="!onboardingInProgress"
              :show-clear-button="false"
            />
          </div>
        </div>

        <div
          id="nav_bar_right_v2"
          class="flex flex-row gap-2 items-center justify-end"
        >
          <NotificationDropdown />

          <div
            class="agi_assistant_container"
            @click="moveToBilling(false)"
            v-if="
              billingVersion === 'v1' &&
              permissions?.includes(permissionConstants.BILLING) &&
              !isPaidPlan() &&
              isFreePlan()
            "
          >
            <div class="agi_assistant_widget">
              <div class="agi_assistant_inner">
                <span class="text_color font-semibold text-sm">Upgrade</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="navbar_right_section" class="flex flex-row gap-2">
      <NavBarProfileSection_v2
        v-if="isUserAuthenticated"
        :billing-version="billingVersion"
        :billing-status="billingStatus"
        :onboarding-in-progress="onboardingInProgress"
        @update-current-workspace="fetchBillingVersion"
      />
      <a-button
        v-else-if="isPublicRoute"
        class="primary_medium mr-2 nav_button px-5"
        @click="navigateToSignUp"
      >
        <ThemeImage
          :icon="imageNames.BOLT_ICON"
          alt="bolt"
          class="size-4"
        />&nbsp;Sign up for free
      </a-button>
    </div>

    <!-- <OutOfCreditsModal
      source="navbar"
      v-model:visible="outOfCreditsModalVisible"
    /> -->

    <FloatingAssistantButton
      v-if="
        billingSetupComplete && isUserAuthenticated && !onboardingInProgress
      "
    />

    <FormTriggerWrapper />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { moveToBilling } from "@/src/utils/utils.js";
import { fetchPermissions } from "@/src/utils/permissionUtils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { permissionConstants } from "@/src/constants/permissionConstants.js";
import {
  checkIfAuthOnboardingInProgress,
  notShowOutOfCreditPopup,
} from "@/src/utils/utils.js";
import {
  checkBillingSetupComplete,
  isFreePlan,
  isPaidPlan,
} from "@/src/utils/billingUtils.js";
import eventBus from "@/src/utils/eventBus.js";
import BillingService from "@/src/api/BillingService.js";
import DashboardService from "@/src/api/DashboardService.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import mqttService from "@/src/services/mqtt/mqttService.js";
import { EventTypes } from "@/src/services/chats/eventManager.js";
import OutOfCreditsModal from "@/src/components/OutOfCreditsModal.vue";
import NavBarProfileSection_v2 from "@/src/components/navbar_components/NavBarProfileSection_v2.vue";
import NotificationDropdown from "@/src/components/notification_components/NotificationDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { useAuthStore } from "@/src/composables/useAuthStore.js";
import FloatingAssistantButton from "@/src/components/navbar_components/FloatingAssistantButton.vue";
import FormTriggerWrapper from "@/src/components/form_components/common/FormTriggerWrapper.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import { isGlobalSearchEnabled } from "@/src/utils/dynamicFormUtils.js";

const router = useRouter();
const route = useRoute();
const store = useStore();
const authStore = useAuthStore();
const searchValue = ref("");
const isSearchPage = ref(false);
const onboardingInProgress = ref(checkIfAuthOnboardingInProgress());
const billingStatus = ref(null);
const trialLeftDays = ref(null);
const workspaceId = ref(null);
const billingVersion = ref("v0");
const outOfCreditsModalVisible = ref(false);
const remainingCredits = ref(null);
const remainingAgentCredits = ref(100);
const userPermissions = ref(
  JSON.parse(localStorage.getItem("userPermissions") || "[]"),
);

const billingSetupComplete = computed(() => checkBillingSetupComplete());
const permissions = computed(() => userPermissions.value);
const isUserAuthenticated = computed(() => authStore.isAuthenticated.value);
const isPublicRoute = computed(() => route.meta.public === true);

const navigateToSignUp = () => {
  window.location.replace("/auth_users");
};

const goToSearch = () => {
  router.push("/search");
};

const handleOnboardingComplete = async () => {
  fetchBillingStatus();
};

const handleBillingSetupComplete = async () => {
  try {
    if (typeof window !== "undefined") {
      const storedPermissions = localStorage.getItem("userPermissions");
      const parsedPermissions = storedPermissions
        ? JSON.parse(storedPermissions)
        : [];

      if (!storedPermissions || parsedPermissions?.length === 0) {
        await fetchPermissions();
        userPermissions.value = JSON.parse(
          localStorage.getItem("userPermissions") || "[]",
        );
      } else {
        await store.dispatch("rbac/fetchPermissions");
        userPermissions.value = parsedPermissions;
      }
    }

    await fetchBillingStatus();
    await fetchBillingVersion();
    await getTotalUsage();
  } catch (error) {
    console.error(
      "An error occurred while handling billing setup complete: ",
      error,
    );
  }
};


const getTotalUsage = async () => {
  try {
    const response = await BillingService.getTotalUsage();
    if (response?.success) {
      remainingAgentCredits.value = response.remaining_agent_credits;
      remainingCredits.value = response.remaining_agent_credits;
    } else {
      console.error("Failed to fetch total usage");
    }
  } catch (error) {
    console.error("An error occurred while fetching total usage:", error);
  }
};

const fetchBillingStatus = async () => {
  try {
    const response = await BillingService.getBillingAccount();

    if (response?.success) {
      if (response?.account) {
        billingStatus.value = response.account.status;
        trialLeftDays.value = response.account.free_trial_left;
        workspaceId.value = response.account.workspace_id;
        await store.dispatch(
          "billing/setAccountStatus",
          response.account.status,
        );

        await store.dispatch(
          "setPaymentStatus",
          response.account.payment_status,
        );
        await store.dispatch(
          "setShowLowCreditsMessage",
          response.account.show_low_credits_message,
        );
      }
    }
  } catch (error) {
    console.error("Error fetching paid status:", error);
    billingStatus.value = null;

    await store.dispatch("billing/setAccountStatus", "");

    await store.dispatch("setPaymentStatus", null);
    await store.dispatch("setShowLowCreditsMessage", null);
  }
};

const fetchBillingVersion = async () => {
  try {
    const response =
      await DashboardService.fetchCurrentWorkspaceBillingVersion();
    if (response?.success) {
      billingVersion.value = response.billing_version;
    }
  } catch (error) {
    console.error("Error fetching billing version:", error);
  }
};

const fetchAppStoreVersion = async () => {
  try {
    await store.dispatch("appStore/fetchAppStoreVersion");
  } catch (error) {
    console.error("Error fetching app store version:", error);
  }
};

const subscribeToNavbarTopics = async () => {
  try {
    const response = await DashboardService.fetchMqttCredentials();
    if (!response.success) {
      console.error("Failed to fetch MQTT credentials for navbar topics");
      return;
    }

    const workspaceId = WorkspaceService.getCurrentWorkspaceId();
    const environment = process.env.NODE_ENV;

    const notificationTopic = `${environment}/notifications/${workspaceId}/user/${response.user_id}`;
    const emailCreatedTopic = `${environment}/email_created/${workspaceId}/user/${response.user_id}`;
    const groupReactionsTopic = `${environment}/workspace/${workspaceId}/group/+/messages`;

    await mqttService.subscribe(notificationTopic, (message) => {
      eventBus.emit("notification-received", message);
    });

    await mqttService.subscribe(emailCreatedTopic, (message) => {
      eventBus.emit("email-created", message);
    });

    await mqttService.subscribe(groupReactionsTopic, (message) => {
      if (message?.event === EventTypes.REACTION_UPDATED || message?.event === EventTypes.THREAD_REACTION_UPDATED) {
        eventBus.emit("chat-sidebar-event", message);
      }
    });
  } catch (error) {
    console.error("Failed to subscribe to navbar MQTT topics:", error);
  }
};

const isNegativeCreditsModalDisabled = computed(
  () => localStorage.getItem("negative_credits_modal_disabled") === "true",
);

const checkOutOfCreditsModal = () => {
  if (isNegativeCreditsModalDisabled.value) return;
  if (route.name === "onboarding") return;

  if (
    billingStatus.value !== "NEW" &&
    remainingCredits.value !== null &&
    remainingCredits.value !== undefined
  ) {
    if (remainingCredits.value <= 500 && notShowOutOfCreditPopup.value) {
      outOfCreditsModalVisible.value = true;
    }
  }
};

watch(
  () => route.path,
  async () => {
    if (billingVersion.value === "v4") {
      await getTotalUsage();
    }
    // checkOutOfCreditsModal();
  },
);

const handleMQTTReady = async () => {
  await subscribeToNavbarTopics();
};

watch(
  () => route.path,
  (newPath) => {
    isSearchPage.value = newPath === "/search";
    onboardingInProgress.value = checkIfAuthOnboardingInProgress();
  },
);

onMounted(async () => {
  if (isPublicRoute.value || !isUserAuthenticated.value) {
    return;
  }

  // Fetch permissions on every page load to ensure they're up-to-date for current workspace
  await fetchPermissions();
  userPermissions.value = JSON.parse(
    localStorage.getItem("userPermissions") || "[]",
  );

  Promise.all([
    fetchBillingStatus(),
    fetchBillingVersion(),
    fetchAppStoreVersion(),
    getTotalUsage(),
  ]).then(() => {
    // checkOutOfCreditsModal();
  });

  eventBus.on("mqtt-connected", handleMQTTReady);

  if (mqttService.isConnected) {
    subscribeToNavbarTopics();
  }

  eventBus.on("onboarding-complete", handleOnboardingComplete);
  eventBus.on("billing-setup-complete", handleBillingSetupComplete);
});

onUnmounted(() => {
  if (isPublicRoute.value || !isUserAuthenticated.value) {
    return;
  }

  eventBus.off("mqtt-connected", handleMQTTReady);
  eventBus.off("onboarding-complete", handleOnboardingComplete);
  eventBus.off("billing-setup-complete", handleBillingSetupComplete);
});
</script>

<style scoped>
.navbar {
  color: white;
  height: 48px;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.nav_button {
  border-radius: 30px !important;
}

.agi_assistant_container {
  margin-right: 0px !important;
}
</style>
